import { useEffect, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';

import { CostCenter } from '~/data/costCenters';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';

export type UseCostCenterPercentageOutput = {
  selectedCostCenterData: CostCenter | undefined;
  additionalSelectValue: number;
  setAdditionalSelectValue: (value: number) => void;
  disabledOption: (id: number) => boolean;
  changeSelectValueToNonSelected: (id?: number) => void;
  totalPercent: number;
  countTotalPercent: (oldValue: number, newValue: number) => void;
  deleteFromTotal: (id: number) => void;
};

export const useCostCenterPercentage = (
  costCenters: CostCenter[],
  selectedCostCenterId: number,
  additionalCostCenters: CostCenter[],
  getValues: UseFormGetValues<Form>,
  index: number,
): UseCostCenterPercentageOutput => {
  const selectedCostCenterData = costCenters.find(
    (cc) => cc.fCostCenterId === selectedCostCenterId,
  );

  const [totalPercent, setTotalPercent] = useState<number>(0);
  const [additionalSelectValue, setAdditionalSelectValue] = useState<number>(
    costCenters.find(
      (cc) =>
        cc.fCostCenterId !== selectedCostCenterId &&
        !additionalCostCenters.find(
          (acc) => acc.fCostCenterId === cc.fCostCenterId,
        ),
    )?.fCostCenterId || selectedCostCenterId,
  );
  const allFormValues = getValues();
  const disabledOption = (id: number) =>
    id === selectedCostCenterId ||
    additionalCostCenters.some((cc) => cc.fCostCenterId === id);

  const changeSelectValueToNonSelected = (id?: number) => {
    const nonSelectedValue = costCenters.find(
      (cc) =>
        (!disabledOption(cc.fCostCenterId) &&
          cc.fCostCenterId !== additionalSelectValue) ||
        (id && cc.fCostCenterId === id),
    );
    setAdditionalSelectValue(
      nonSelectedValue ? nonSelectedValue.fCostCenterId : -1,
    );
  };

  const countTotalPercent = (oldValue: number, newValue: number) => {
    const calculateOldValue = oldValue ? oldValue : 0;
    const calculateNewValue = newValue ? newValue : 0;
    setTotalPercent(totalPercent - calculateOldValue + calculateNewValue);
  };

  const deleteFromTotal = (id: number) => {
    const ccPercent =
      allFormValues.schedule?.[index]?.empPosition?.[`${id}-cc-percent`];
    setTotalPercent(
      totalPercent - (ccPercent && ccPercent !== '' ? parseInt(ccPercent) : 0),
    );
  };

  useEffect(() => {
    const addRegisteredPercent = additionalCostCenters.reduce((acc, cc) => {
      const ccPercent =
        allFormValues.schedule?.[index]?.empPosition?.[
          `${cc.fCostCenterId}-cc-percent`
        ];
      return acc + (ccPercent && ccPercent !== '' ? parseInt(ccPercent) : 0);
    }, 0);
    const ccPercent =
      allFormValues.schedule?.[index]?.empPosition?.[
        `${selectedCostCenterId}-cc-percent`
      ];
    setTotalPercent(
      addRegisteredPercent +
        (ccPercent && ccPercent !== '' ? parseInt(ccPercent) : 0),
    );
  }, [
    additionalCostCenters,
    allFormValues,
    getValues,
    index,
    selectedCostCenterId,
  ]);

  return {
    selectedCostCenterData,
    setAdditionalSelectValue,
    additionalSelectValue,
    disabledOption,
    changeSelectValueToNonSelected,
    totalPercent,
    countTotalPercent,
    deleteFromTotal,
  };
};
